import { useMemo } from "react";
import { create } from "zustand";
import { useProfiles } from "./profileStore.js";
import { persist } from "zustand/middleware";

/**
 * Parental controls (category-based).
 *
 * WHY CATEGORIES AND NOT RATINGS: `categoryId` is a slugified provider category
 * name, present on every channel, movie and series with no network call, so a
 * locked category can be filtered out BEFORE anything renders. TMDB
 * certifications do exist and are well populated, but they arrive from a
 * per-title details call — you cannot fetch 43,850 of them to decide what to
 * put in a rail, so a rating filter would render tiles and then retract them,
 * which is the exact leak this feature exists to prevent. Live TV has no TMDB
 * match at all. This is why TiviMate and Smarters lock categories too.
 *
 * THREAT MODEL: this keeps a child out of adult categories. It is NOT a
 * security boundary. The PIN is hashed rather than stored in the clear so it
 * is not readable at a glance, but a 4-digit PIN has 10,000 combinations and
 * anyone with devtools can edit localStorage. Do not present it as more than
 * it is.
 */

/** Words that mark a category as adult, matched on slug segment boundaries so
 * "adults-only" and "xxx-movies" hit while "adulthood" and "sexton-blake" do
 * not. Slugs are lower-case and hyphenated (see categoryId() in
 * packages/iptv-parser). */
const ADULT_WORD_PATTERN =
  /(^|-)(xxx+|adult|adults|porn|porno|pornhub|erotic|erotica|sexy|hentai|nsfw|playboy|brazzers|onlyfans)(-|$)/i;

/**
 * Age markers, which need a TIGHTER rule than the words above. A bare "18"
 * appears in ordinary titles and categories -- "18-again-1988" is the 1988 film
 * "18 Again" -- and hiding those silently is worse than missing one adult
 * category, because nothing tells the parent that content disappeared. So a
 * bare number only counts when it IS the whole slug; otherwise it must carry an
 * explicit plus marker.
 */
const AGE_EXACT = new Set(["18", "21", "18+", "21+"]);
const AGE_MARKER_PATTERN = /(^|-)(18|21)(\+|-plus|plus)(-|$)/i;

/** True when a category slug looks like adult content by name alone. */
export function looksAdultCategory(categoryId: string): boolean {
  const slug = categoryId.trim().toLowerCase();
  if (AGE_EXACT.has(slug)) return true;
  if (AGE_MARKER_PATTERN.test(slug)) return true;
  return ADULT_WORD_PATTERN.test(slug);
}

export interface ParentalStore {
  /** Hashed PIN; null means parental controls are off. */
  pinHash: string | null;
  /** Hide categories whose slug looks adult, without listing them by hand. */
  autoLockAdult: boolean;
  /** Category slugs the parent locked explicitly. */
  lockedCategoryIds: string[];
  /** Epoch ms until which locked content is visible after a correct PIN. */
  unlockedUntil: number | null;
  setPin: (pin: string) => Promise<void>;
  clearPin: () => void;
  verifyPin: (pin: string) => Promise<boolean>;
  setAutoLockAdult: (on: boolean) => void;
  toggleCategory: (categoryId: string) => void;
  /** Reveal locked content for a while after a correct PIN. */
  unlockFor: (ms: number) => void;
  relock: () => void;
}

/** SHA-256 where available, fnv-1a otherwise. See the threat-model note: this
 * stops shoulder-surfing and casual snooping, not a determined attacker. */
export async function hashPin(pin: string): Promise<string> {
  const salted = `slique-parental-v1|${pin}`;
  const subtle = globalThis.crypto?.subtle;
  if (subtle) {
    const bytes = new TextEncoder().encode(salted);
    const digest = await subtle.digest("SHA-256", bytes);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  let hash = 0x811c9dc5;
  for (let i = 0; i < salted.length; i += 1) {
    hash ^= salted.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return `fnv-${(hash >>> 0).toString(16).padStart(8, "0")}`;
}

export const useParental = create<ParentalStore>()(
  persist(
    (set, get) => ({
      pinHash: null,
      autoLockAdult: true,
      lockedCategoryIds: [],
      unlockedUntil: null,

      setPin: async (pin) => {
        set({ pinHash: await hashPin(pin) });
      },
      clearPin: () => set({ pinHash: null, unlockedUntil: null }),
      verifyPin: async (pin) => {
        const { pinHash } = get();
        if (!pinHash) return false;
        return (await hashPin(pin)) === pinHash;
      },
      setAutoLockAdult: (autoLockAdult) => set({ autoLockAdult }),
      toggleCategory: (categoryId) =>
        set((state) => ({
          lockedCategoryIds: state.lockedCategoryIds.includes(categoryId)
            ? state.lockedCategoryIds.filter((id) => id !== categoryId)
            : [...state.lockedCategoryIds, categoryId],
        })),
      unlockFor: (ms) => set({ unlockedUntil: Date.now() + ms }),
      relock: () => set({ unlockedUntil: null }),
    }),
    {
      name: "slique-parental",
      // Deliberately does NOT persist a "currently unlocked" window: a restart
      // must relock. unlockedUntil is kept in memory only.
      partialize: (state) => ({
        pinHash: state.pinHash,
        autoLockAdult: state.autoLockAdult,
        lockedCategoryIds: state.lockedCategoryIds,
      }),
    },
  ),
);

/**
 * Whether a category is currently hidden. Pure so it can be tested and reused
 * without a React context.
 */
export interface ParentalFilterState
  extends Pick<ParentalStore, "pinHash" | "autoLockAdult" | "lockedCategoryIds" | "unlockedUntil"> {
  /** Active profile is a Kids profile. Restricts regardless of PIN — a Kids
   * profile that only protects children once a parent remembers to set a PIN
   * is not a Kids profile. */
  kidsMode?: boolean;
}

export function isCategoryLocked(
  categoryId: string | undefined,
  state: ParentalFilterState,
  now = Date.now(),
): boolean {
  // Kids mode restricts on its own; otherwise no PIN means the feature is off.
  if (!state.pinHash && !state.kidsMode) return false;
  // The unlock window is a parent action on a parent profile. It must NOT
  // follow them into a Kids profile: otherwise unlocking, handing over the
  // device and letting the child switch profiles leaves adult content visible
  // for the rest of the timer.
  if (!state.kidsMode && state.unlockedUntil !== null && state.unlockedUntil > now) return false;
  if (!categoryId) return false;
  if (state.lockedCategoryIds.includes(categoryId)) return true;
  // Kids mode forces adult auto-detection on even if the parent turned it off.
  return (state.autoLockAdult || state.kidsMode === true) && looksAdultCategory(categoryId);
}

/** Filter helper for catalog lists. */
export function filterLockedItems<T extends { categoryId?: string }>(
  items: T[],
  state: ParentalFilterState,
): T[] {
  if (!state.pinHash && !state.kidsMode) return items;
  if (!state.kidsMode && state.unlockedUntil !== null && state.unlockedUntil > Date.now()) return items;
  return items.filter((item) => !isCategoryLocked(item.categoryId, state));
}

/**
 * The four fields the filters need, as one stable object.
 *
 * Selected field-by-field on purpose: a zustand selector returning a fresh
 * object literal changes identity on every store write and re-renders every
 * consumer. useMemo over the primitives keeps the reference stable until
 * something the filter actually depends on changes.
 */
export function useParentalFilter(): ParentalFilterState {
  const pinHash = useParental((s) => s.pinHash);
  const autoLockAdult = useParental((s) => s.autoLockAdult);
  const lockedCategoryIds = useParental((s) => s.lockedCategoryIds);
  const unlockedUntil = useParental((s) => s.unlockedUntil);
  const kidsMode = useProfiles((s) => s.profiles.find((p) => p.id === s.activeProfileId)?.isKids === true);
  return useMemo(
    () => ({ pinHash, autoLockAdult, lockedCategoryIds, unlockedUntil, kidsMode }),
    [pinHash, autoLockAdult, lockedCategoryIds, unlockedUntil, kidsMode],
  );
}
